import { useMemo } from 'react';

import { type ContentKey, mediaSrc, useContent } from './ContentProvider';

/**
 * The homepage's editable items, resolved for rendering.
 *
 * Components draw; this decides what there is to draw. Visibility, order,
 * links and image overrides are content rules, and keeping them here means a
 * section component never parses a content value itself.
 */

/** A section or a card is drawn unless an editor has published "hidden". */
export function isShown(value: string): boolean {
  return value !== 'hidden';
}

/**
 * Sorts slots by their published position, keeping design order for ties and
 * for any value that is not a position — so a malformed value can move a card
 * to the end of nothing, never drop it.
 */
export function bySlotOrder<T>(slots: readonly T[], position: (slot: T) => string): readonly T[] {
  return slots
    .map((slot, index) => {
      const parsed = Number.parseInt(position(slot), 10);
      return { slot, index, rank: Number.isFinite(parsed) ? parsed : index + 1 };
    })
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.slot);
}

/**
 * Where an editable button goes, or `undefined` for "coming soon".
 *
 * The server already refuses anything but a relative path or an http(s) URL;
 * this re-checks on the way out, so a value that reached storage some other way
 * still cannot become a `javascript:` link on the homepage.
 */
export function safeHref(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  if (trimmed.startsWith('/')) return trimmed.startsWith('//') ? undefined : trimmed;
  try {
    const { protocol } = new URL(trimmed);
    return protocol === 'http:' || protocol === 'https:' ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

export interface HomepageContent {
  /** Reads one block. */
  readonly text: (key: string) => string;
  /** An uploaded image if the block holds one, else the design's shipped file. */
  readonly image: (key: string, shipped: string) => { readonly src: string; readonly uploaded: boolean };
  readonly href: (key: string) => string | undefined;
  readonly shown: (key: string) => boolean;
  readonly ordered: <T>(slots: readonly T[], orderKey: (slot: T) => string) => readonly T[];
}

export function useHomepageContent(): HomepageContent {
  const content = useContent();

  return useMemo(() => {
    const text = (key: string): string => content(key as ContentKey);
    return {
      text,
      image: (key, shipped) => {
        const uploaded = mediaSrc(text(key));
        return uploaded === undefined ? { src: shipped, uploaded: false } : { src: uploaded, uploaded: true };
      },
      href: (key) => safeHref(text(key)),
      shown: (key) => isShown(text(key)),
      ordered: (slots, orderKey) =>
        bySlotOrder(slots, (slot) => text(orderKey(slot))).filter((slot) =>
          isShown(text(orderKey(slot).replace(/order$/, 'visible'))),
        ),
    };
  }, [content]);
}

/**
 * Which of the homepage's sections are switched on.
 *
 * The page needs this as a whole, not section by section: three sections are
 * sticky panels that slide over the one before, and whether a panel overlaps
 * depends on whether there is a panel before it to overlap.
 */
export interface SectionVisibility {
  readonly explore: boolean;
  readonly os: boolean;
  readonly solutions: boolean;
  readonly portal: boolean;
  readonly superapp: boolean;
  readonly grow: boolean;
  readonly refer: boolean;
  readonly download: boolean;
  readonly news: boolean;
}

export function useSectionVisibility(): SectionVisibility {
  const { shown } = useHomepageContent();
  return {
    explore: shown('explore.visible'),
    os: shown('os.visible'),
    solutions: shown('solutions.visible'),
    portal: shown('portal.visible'),
    superapp: shown('superapp.visible'),
    grow: shown('grow.visible'),
    refer: shown('refer.visible'),
    download: shown('download.visible'),
    news: shown('news.visible'),
  };
}
